import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Step components
import WelcomeStep from './onboarding/WelcomeStep';
import PersonalInfoStep from './onboarding/PersonalInfoStep';
import JobSearchStep from './onboarding/JobSearchStep';
import SkillsStep from './onboarding/SkillsStep';
import LinksStep from './onboarding/LinksStep';
import ResumeUploadStep from './onboarding/ResumeUploadStep';
import PaymentStep from './onboarding/PaymentStep';

function OnboardingFlow() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Current step (0-indexed) - always start fresh
  const [currentStep, setCurrentStep] = useState(0);
  
  // Onboarding data state
  const [onboardingData, setOnboardingData] = useState({
    // Personal Info
    firstName: '',
    lastName: '',
    
    // Job Search
    employmentStatus: '',
    jobSearchStatus: '',
    
    // Skills
    roleKeywords: [],
    currentSkills: [],
    softwareFamiliarity: [],
    
    // Professional Links (optional)
    professionalLinks: {
      linkedin: '',
      github: '',
      website: ''
    },
    
    // Resume upload
    resumeFile: null,
    
    // Payment
    stripeCustomerId: '',
    subscriptionId: ''
  });

  // Step configuration
  const steps = [
    { component: WelcomeStep, title: 'Welcome to Elena', optional: false },
    { component: PersonalInfoStep, title: 'Personal Information', optional: false },
    { component: JobSearchStep, title: 'Job Search Status', optional: false },
    { component: SkillsStep, title: 'Skills & Keywords', optional: false },
    { component: LinksStep, title: 'Professional Links', optional: true },
    { component: ResumeUploadStep, title: 'Upload Resume', optional: true },
    { component: PaymentStep, title: 'Start Your Trial', optional: false }
  ];

  const totalSteps = steps.length;
  const progressPercentage = ((currentStep + 1) / totalSteps) * 100;

  // Always start onboarding from the beginning with fresh data
  useEffect(() => {
    setCurrentStep(0);
    // Reset onboarding data to start fresh
    setOnboardingData({
      firstName: '',
      lastName: '',
      employmentStatus: '',
      jobSearchStatus: '',
      roleKeywords: [],
      currentSkills: [],
      softwareFamiliarity: [],
      professionalLinks: {
        linkedin: '',
        github: '',
        website: ''
      },
      resumeFile: null,
      stripeCustomerId: '',
      subscriptionId: ''
    });
  }, []);

  // Initialize data from user if already logged in
  useEffect(() => {
    if (user) {
      setOnboardingData(prev => ({
        ...prev,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        employmentStatus: user.employmentStatus || '',
        jobSearchStatus: user.jobSearchStatus || '',
        roleKeywords: user.roleKeywords || [],
        currentSkills: user.currentSkills || [],
        softwareFamiliarity: user.softwareFamiliarity || [],
        professionalLinks: {
          linkedin: user.professionalLinks?.linkedin || '',
          github: user.professionalLinks?.github || '',
          website: user.professionalLinks?.website || ''
        }
      }));
    }
  }, [user]);

  // Navigation functions
  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < totalSteps) {
      setCurrentStep(stepIndex);
    }
  };

  const skipStep = () => {
    if (steps[currentStep]?.optional) {
      nextStep();
    }
  };

  // Update onboarding data
  const updateData = (newData) => {
    setOnboardingData(prev => ({
      ...prev,
      ...newData
    }));
  };

  // Complete onboarding
  const completeOnboarding = async () => {
    try {
      // TODO: Save final data to backend
      // TODO: Mark user as onboarded
      console.log('Onboarding completed with data:', onboardingData);
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  };

  // Get current step component
  const CurrentStepComponent = steps[currentStep]?.component;

  if (!CurrentStepComponent) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 w-full z-50">
        <div className="bg-gray-900 bg-opacity-90 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                {currentStep > 0 && (
                  <button
                    onClick={prevStep}
                    className="flex items-center text-gray-300 hover:text-white transition-colors duration-200 mr-4"
                  >
                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back
                  </button>
                )}
                <div className="text-white text-sm font-medium">
                  Step {currentStep + 1} of {totalSteps}
                </div>
              </div>
              <div className="text-gray-300 text-sm">
                {steps[currentStep]?.title}
              </div>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-24 pb-8 px-6 min-h-screen">
        <div className="max-w-2xl mx-auto">
          <CurrentStepComponent
            data={onboardingData}
            updateData={updateData}
            nextStep={nextStep}
            prevStep={prevStep}
            skipStep={skipStep}
            goToStep={goToStep}
            currentStep={currentStep}
            totalSteps={totalSteps}
            isFirstStep={currentStep === 0}
            isLastStep={currentStep === totalSteps - 1}
            isOptional={steps[currentStep]?.optional}
            completeOnboarding={completeOnboarding}
          />
        </div>
      </div>
    </div>
  );
}

export default OnboardingFlow; 